// Ids of defense-plan features drawn in THIS session, so the map layer and
// the panel list can play a one-time entrance for them and for nothing else.
// A feature that arrives from a Firestore snapshot (page load, another
// device, a plan switch) never gets in here, so it never animates.
//
// addFeature marks the id; the map layer and the panel list each take it
// once ("layer" and "row"). Entries also expire, so a mark that nobody
// takes (the panel is closed, the layer isn't mounted) can't animate a
// shape minutes later.

export type RecentPlanFeatureConsumer = 'layer' | 'row';

const TTL_MS = 10_000;

const marks = new Map<string, { at: number; taken: Set<RecentPlanFeatureConsumer> }>();

function sweep(now: number): void {
  for (const [id, mark] of marks) {
    if (now - mark.at > TTL_MS) marks.delete(id);
  }
}

export function markPlanFeatureAddedLocally(id: string): void {
  const now = Date.now();
  sweep(now);
  marks.set(id, { at: now, taken: new Set() });
}

/** True the first time each consumer asks about a freshly drawn feature,
 *  false for every later call and for any id that wasn't drawn here. */
export function takeRecentlyAddedPlanFeature(
  id: string,
  consumer: RecentPlanFeatureConsumer
): boolean {
  const mark = marks.get(id);
  if (!mark) return false;
  if (Date.now() - mark.at > TTL_MS || mark.taken.has(consumer)) {
    if (Date.now() - mark.at > TTL_MS) marks.delete(id);
    return false;
  }
  mark.taken.add(consumer);
  if (mark.taken.size >= 2) marks.delete(id);
  return true;
}

export function resetRecentPlanFeatures(): void {
  marks.clear();
}
